import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { FAQApiService } from '../services/faqApiService';
import { useFAQPermissions } from '../../../hooks/usePermissions';

// Query Keys
export const FAQ_QUERY_KEYS = {
  all: ['faqs'] as const,
  lists: () => [...FAQ_QUERY_KEYS.all, 'list'] as const,
  list: (filters?: any, page?: number) => [...FAQ_QUERY_KEYS.lists(), { filters, page }] as const,
  stats: () => [...FAQ_QUERY_KEYS.all, 'stats'] as const,
} as const;

export const useFAQs = (filters?: any, page = 1) => {
  const { canCreateFAQ } = useFAQPermissions();

  return useQuery({
    queryKey: FAQ_QUERY_KEYS.list(filters, page),
    queryFn: () => FAQApiService.getFAQs(filters, page),
    enabled: canCreateFAQ,
    staleTime: 5 * 60 * 1000,
  });
};

export const useFAQStats = () => {
  const { canViewStats } = useFAQPermissions();

  return useQuery({
    queryKey: FAQ_QUERY_KEYS.stats(),
    queryFn: FAQApiService.getFAQStats,
    enabled: canViewStats,
    refetchInterval: 30000,
  });
};

export const useCreateFAQ = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: FAQApiService.createFAQ,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FAQ_QUERY_KEYS.lists() });
      enqueueSnackbar('FAQ créée avec succès', { variant: 'success' });
    },
    onError: (error: any) => {
      enqueueSnackbar('Erreur lors de la création', { variant: 'error' });
    },
  });
};

export const useUpdateFAQ = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => FAQApiService.updateFAQ(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FAQ_QUERY_KEYS.lists() });
      enqueueSnackbar('FAQ mise à jour avec succès', { variant: 'success' });
    },
    onError: (error: any) => {
      enqueueSnackbar('Erreur lors de la mise à jour', { variant: 'error' });
    },
  });
};

export const useDeleteFAQ = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: FAQApiService.deleteFAQ,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FAQ_QUERY_KEYS.lists() });
      enqueueSnackbar('FAQ supprimée avec succès', { variant: 'success' });
    },
  });
};

export const useTestAgent = () => {
  return useMutation({
    mutationFn: FAQApiService.testAgent,
  });
};
